import moment from "moment";
import qs from "query-string";
import { capitalize, isEmpty, get } from "lodash";

import {
	isArray,
	isNonEmptyArray,
	isNonEmptyString,
	isNumber,
	isObjectWithKey,
	isNonEmptyObject,
} from "@cayuse/shared-react.utils.type-checks";

import {
	API_AWARD,
	API_PROPOSAL,
	COLUMN_HEADER_KEYS,
	DEFAULT_CURRENCY,
	DEFAULT_LOCALE,
	DEFAULT_SEARCH_LIMIT,
	SYS_DATES_KEYS,
	FORM_SUBMISSION_TYPES,
	AWARDS_BASE_ROUTE,
	PROPOSALS_BASE_ROUTE,
	AWARDS, API_PROJECT,
	SUBAWARDS,
	SUBAWARDS_ALT,
	SUBAWARDS_BASE_ROUTE
} from "globals/constants";

import { request } from "./proxy";

export const camelToKebabCase = str => str.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);

export const kebabToCamelCase = str => str.replace(/-([a-z])/g, g => { return g[1].toUpperCase(); });

export const stringifyJSON = value => !!value ? JSON.stringify(value) : undefined;

export const parseJSON = json => {
	const parsedData = isNonEmptyString(json)
		? JSON.parse(json)
		: undefined;

	return !!parsedData ? parsedData : undefined;
};

// preserve the selected calendar day for displaying a day in a different timezone
export const momentToCalendarDay = (date, tz) => {
	return moment.isMoment(date) ? moment.tz([ date.year(), date.month(), date.date() ], tz) : date;
};

/**
 * Note, the escaped characters are needed for the regex to work right
 */
export const trim = (string, target = "\s") => {    // eslint-disable-line no-useless-escape
	if (isNonEmptyString(string)) {
		const pattern = `(^${target}+)|(${target}+\$)`; // eslint-disable-line no-useless-escape
		const regex = new RegExp(pattern, "g");

		return string.replace(regex, "");
	}

	return string;
};


export const isSystemGeneratedDate = keys => {
	for (const key of keys) {
		if ( isNonEmptyString(key) && SYS_DATES_KEYS.includes(key)) {
			return true;
		}
	}

	return false;
};

export const isValueUnique = (array, value, type) => {
	if (isNonEmptyString(type)) {
		switch (type) {
		case "string": {
			if (!isNonEmptyString(value)) {
				return false;
			}
			const lcValue = value.trim().toLowerCase();

			return !array.some(arrayValue => arrayValue.trim().toLowerCase() === lcValue);
		}
		default:
			return false;
		}
	}

	return false;
};

export const validateTitle = (title, titleObjects) => {
	const trimmedTitle = title.trim();
	const isTitleNotEmpty = isNonEmptyString(trimmedTitle);

	const titles = titleObjects.map(titleObject => titleObject.value);
	const isSameTitleNotPresent = isValueUnique(titles, title, "string");

	return { trimmedTitle, isTitleNotEmpty, isSameTitleNotPresent };
};

/**
 * substitutions should be an array of arrays containing search / replace pairs, where search is a string or regex
 * ex: { substitutions: [
 *     [ "#", "number" ],
 *     [ "\D"g, "" ]
 * ]}
 */
export const slugifyString = (value, { substitutions } = {}) => {
	let slug = value;

	if (isNonEmptyString(value)) {
		slug = value
			.replace(/([a-z])([A-Z])/g, "$1 $2") // Convert camelCase to space-separated words
			.toLowerCase();

		if (isNonEmptyArray(substitutions)) {
			substitutions
				.filter(substitution => {
					return isNonEmptyArray(substitution) && substitution.length === 2;
				})
				.forEach(([ search, replace ]) => {
					slug = slug.replace(search, replace);
				});
		}

		slug = slug.replace(/[^a-z0-9\s-]/g, "")
			.replace(/[\s]+/g, "-");
	}

	return slug;
};

export const addAttributesToTabs = tabsList =>{
	tabsList.forEach(tab => {
		if (!tab.classList.contains("tab-element")) {
			tab.classList.add("tab-element");
		}
		tab.tabIndex = 0;
	});
};

export const handleAccessToTabs = (tabsList, mode) => {
	const handleEnter = event =>{
		if (event.key === "Enter") {
			if( event.target.classList.contains("tab-element")) {
				event.target.click();
				addAttributesToTabs(tabsList);
			}
		};
	};
	addAttributesToTabs(tabsList);
	if (mode === "add") {
		tabsList.forEach(tab => {
			document.addEventListener("keydown", handleEnter);
		});
	} else if (mode === "remove") {
		tabsList.forEach(tab => {
			document.removeEventListener("keydown", handleEnter);
		});
	}
};

export const formattedCurrency = (
	value,
	currency = DEFAULT_CURRENCY,
	locale = DEFAULT_LOCALE
) => {
	value = parseFloat(value);
	const formattedCurrency = (isNumber(value) && value !== 0 && !isNaN(value))
		? new Intl.NumberFormat(locale, { style: "currency", currency: currency }).format(value)
		: "";

	return isNonEmptyString(formattedCurrency)
		? formattedCurrency
		: "--";
};

export const ucFirst = value => {
	return isNonEmptyString(value)
		? value.charAt(0).toUpperCase() + value.slice(1)
		: value;
};

export const sortByAlpha = (a, b) => {
	const valueA = a.value.toUpperCase().trim();
	const valueB = b.value.toUpperCase().trim();

	return valueA.localeCompare(valueB);
};

export const sortCollectionByKey = (collection, key) => {
	return collection.sort((a, b) => {
		const valueA = isNonEmptyString(a[key])
			? a[key].toLowerCase()
			: "";

		const valueB = isNonEmptyString(b[key])
			? b[key].toLowerCase()
			: "";

		return valueA > valueB ? 1 : -1;
	});
};

export const sortCollectionByNestedKey = (collection, key) => {
	return collection.sort((a, b) => {
		const valueA = isNonEmptyString(get(a, key))
			? get(a, key).toLowerCase()
			: "";

		const valueB = isNonEmptyString(get(b, key))
			? get(b, key).toLowerCase()
			: "";

		return valueA > valueB ? 1 : -1;
	});
};

export const alphaSortCollectionByKey = (collection, key) => {
	collection.sort((a, b) => {
		const valueA = isNonEmptyString(a[key])
			? a[key].toLowerCase()
			: "";

		const valueB = isNonEmptyString(b[key])
			? b[key].toLowerCase()
			: "";

		return valueA.localeCompare(valueB);
	});
};

const mapKeyValPairs = searchTerms => {
	const hash = {};

	searchTerms.forEach(({ key, val }) => {
		if (isNonEmptyString(key)) {
			hash[key] = val;
		}
	});

	return hash;
};

const processSingleSearchTerm = searchTerm => {
	return isObjectWithKey(searchTerm, "key") ? { [searchTerm.key]: searchTerm.val } : searchTerm;
};

export const encodeSearchTerms = (
	searchTerms, /* object like { key: val } OR an object like { key: string, val: string } OR array of key/values like [{ key: string, val: string }] */
	{ shouldEncodeUri } = { shouldEncodeUri: true }
) => {
	const encodedFragments = [];
	const searchHash = isArray(searchTerms) ? mapKeyValPairs(searchTerms) : processSingleSearchTerm(searchTerms);

	Object.keys(searchHash)
		.forEach(key => {
			const val = searchHash[key];

			if (isNonEmptyString(val)) {
				const keyValuePair = `${key}:${escapeForElasticSearchQuery(val)}`;
				encodedFragments.push(shouldEncodeUri !== false
					? encodeURIComponent(keyValuePair)
					: keyValuePair
				);
			}
		});
	return encodedFragments.join(",");
};

export const buildSearchTerms = query => {
	let string = "";

	if (isArray(query)) {
		string = JSON.stringify(query.map(({ key, val }) => ({ [key]: val })));
	} else if (isNonEmptyObject(query)) {
		string = JSON.stringify(processSingleSearchTerm(query));
	}

	return encodeURIComponent(string);
};

export const logMissingFactoryConstant = key => console.error("No '%s' constant available, unable to use factory action", key);

export const spUrl = url => url && !url.includes("/sp") ? `/sp/${url.replace(/^\//, "")}` : url;

export const getValueFromObjectByKeys = (obj, keys) => {
	const keyWithContent = isNonEmptyArray(keys) && !!obj
		? keys.find(key => !!obj[key])
		: undefined;

	return isNonEmptyString(keyWithContent)
		? obj[keyWithContent]
		: "";
};

export const getKeysFromColumnHeaders = columnHeaders => {
	const requestedFieldKeyMap = new Map();

	if (isNonEmptyArray(columnHeaders.filter(Boolean))) {
		columnHeaders
			.filter(({ keys }) => isNonEmptyArray(keys))
			.forEach(({ keys }) => {
				keys.forEach(key => requestedFieldKeyMap.set(key, key));
			});
	}

	return Array.from(requestedFieldKeyMap.keys());
};

export const getDateFromTimestamp = timestamp => {
	return timestamp ? moment(timestamp) : moment();
};

export const debounce = (callback, wait) => {
	let timeout;

	return (...args) => {
		const context = this;
		clearTimeout(timeout);
		timeout = setTimeout(() => callback.apply(context, args), wait);
	};
};

export const generateDatedDownloadFileName = ({ prefix, extension }) => {
	const formattedPrefix = isNonEmptyString(prefix)
		? `${prefix}_`
		: "";
	const formattedExtension = isNonEmptyString(extension)
		? `.${extension}`
		: "";
	const dateString = moment().format("YYYY_MMM_DD");

	return `${formattedPrefix}${dateString}${formattedExtension}`;
};

export const getQuerystringValue = key => {
	const params = qs.parse(window.location.search);

	return key in params ? params[key] : undefined;
};

export const getElementAncestorComponent = (initialElement, targetComponentName) => {
	if (initialElement.dataset?.component?.toLowerCase() === targetComponentName) {
		return initialElement;
	}

	let parent = initialElement.parentElement;

	while (parent) {
		const componentName = parent.dataset?.component;

		if (componentName === targetComponentName) {
			return parent;
		}

		parent = parent.parentElement;
	}

	return undefined;
};

export const createFromOptions = ({
	fields,
	searchFields,
	searchQuery,
	formType,
	size = DEFAULT_SEARCH_LIMIT,
	page = 0,
	fromProject = false,
	projectId = null
}) => {
	const attributes = `attributes=${searchFields}`;
	page = `page=${page}`;
	const requiredFields = `requiredAttributes=${fields}`;
	const search = `search=${encodeURIComponent(searchQuery)}`;
	size = `size=${size}`;

	let baseApiUrl = getApiBaseRoute(formType, fromProject);
	if(fromProject) {
		baseApiUrl = spUrl(`${API_PROJECT}/${projectId}/proposals`);
	}

	const searchString = `attributes?${search}&${attributes}&${requiredFields}&${size}&${page}`;
	const url = `${baseApiUrl}/${searchString}`;

	return request(url)
		.then(result => {
			const { content, totalElements } = result;

			return { content, totalElements };
		}).catch(error => {
			console.error(error); // TODO: show error to user or ?
		});
};

export const getApiBaseRoute = formType => {
	switch(formType.toUpperCase()) {
	case COLUMN_HEADER_KEYS.PROPOSAL:
		return spUrl(API_PROPOSAL);
	case COLUMN_HEADER_KEYS.AWARD:
		return spUrl(API_AWARD);
	case COLUMN_HEADER_KEYS.PROJECT:
		return spUrl(API_PROJECT);
	default:
		return "";
	}
};

export const getBaseRoute = formType => {
	switch(formType) {
	case FORM_SUBMISSION_TYPES.AWARD:
		return spUrl(AWARDS_BASE_ROUTE);
	case FORM_SUBMISSION_TYPES.PROPOSAL:
		return spUrl(PROPOSALS_BASE_ROUTE);
	default:
		return "";
	}
};

export const findNestedValueInObject = (payloadContent, fieldKey, searchValue) => {
	var result = null;
	if(payloadContent instanceof Array) {
		for(var i = 0; i < payloadContent.length; i++) {
			result = findNestedValueInObject(payloadContent[i], fieldKey, searchValue);
			if (result) {
				break;
			}
		}
	} else {
		for(var prop in payloadContent) {
			if(prop === fieldKey) {
				if(payloadContent[prop] === searchValue) {
					return payloadContent;
				}
			}
			if(payloadContent[prop] instanceof Object || payloadContent[prop] instanceof Array) {
				result = findNestedValueInObject(payloadContent[prop], fieldKey, searchValue);
				if (result) {
					break;
				}
			}
		}
	}

	return result;
};

export const reportsTypeCheck = (type, awardValue, subawardValue, proposalValue) => {
	switch (type) {
	case AWARDS:
		return awardValue;
	case SUBAWARDS_ALT:
		return subawardValue;
	default:
		return proposalValue;
	}
};

export const isNonEmpty = object => !isEmpty(object);

export const validateTitleLength = title => title?.length < 256;

export const capitalizeAndJoin = (items, splitter = "_", separator = ", ") => {
	return Array.isArray(items)
		? items.map(item => item.split(splitter).map(word => capitalize(word)).join(" "))
			.join(separator)
		: "";
};

export const convertToPlainText = string => {
	if (string.length > 0) {
		return string
			.replace(/(<([^>]+)>)/ig, "")
			.replace(/&(?:nbsp|amp|gt|lt);/g, match => ({
				"&nbsp;": " ",
				"&amp;": "&",
				"&gt;": ">",
				"&lt;": "<"
			}[match]))
			.trim();
	}

	return "";
};

export const escapeForElasticSearchQuery = value =>
	value.replace(/\\/g, "\\\\").replace(/,/g, "\\,");

export const getBackUrl = ({ submissionType, recordId, recordUrl }) =>
  submissionType === SUBAWARDS
    ? spUrl(`${SUBAWARDS_BASE_ROUTE}/${recordId}`)
    : recordUrl;

export const redirectBackToTaskTab = (history, args) => {
  const base = getBackUrl(args);
  history.push(`${base}?tab=tasks`);
};

/**
 * Builds a composite key for an option object based on its id, columnHeader, and specialKey.
 * @param {Object} option - The option object containing id, columnHeader, and optionally specialKey.
 * @param {string} [option.id] - The unique identifier of the option.
 * @param {string} [option.columnHeader] - The column header of the option.
 * @param {string} [option.specialKey] - A special key for the option, if applicable.
 * @returns {string} A composite key in the format `id:${id}|specialKey:${specialKey}` or `id:${id}|columnHeader:${columnHeader}`.
 */
export const buildCompositeKey = (option) => {
	const id = option?.id || "";
	const columnHeader = option?.columnHeader || "";
	const specialKey = option?.specialKey || "";

	return specialKey ? `id:${id}|specialKey:${specialKey}` : `id:${id}|columnHeader:${columnHeader}`;
};

export const FOCUSABLE_SELECTORS = [
	"a[href]",
	"button:not([disabled])",
	"input:not([disabled])",
	"select:not([disabled])",
	"textarea:not([disabled])",
	"[tabindex]:not([tabindex='-1'])",
].join(", ");

export const handleFocusTrap = (e, node) => {
    if (e.key !== "Tab") { return; }

    const focusableElements = Array.from(node.querySelectorAll(FOCUSABLE_SELECTORS));

    if (focusableElements.length === 0) { return; }

    const firstEl = focusableElements[0];
    const lastEl = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
        // Shift+Tab: wrap from first to last
        if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl.focus();
        }
    } else {
        // Tab: wrap from last to first
        if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl.focus();
        }
    }
};